import { cn } from '@heidi/utils';

import type { ToastTone, ToastVariant } from './toast.types';

// Solid variant tone classes (System Toast)
export const solidToneClasses: Record<ToastTone, string> = {
  default: cn('border-background-primary bg-background-primary text-text-primary-invert'),
  subtle: cn('border-background-secondary bg-background-secondary text-text-primary'),
  destructive: cn(
    'border-validation-destructive bg-validation-destructive text-text-primary-invert'
  ),
  success: cn('border-validation-success bg-validation-success text-text-primary-invert'),
  pro: cn('border-tier-pro bg-tier-pro text-text-primary-invert'),
  practice: cn('border-tier-practice bg-tier-practice text-text-primary-invert'),
  warning: cn('border-validation-warning bg-validation-warning text-text-primary-invert'),
};

// Outline variant tone classes (Callout Toast)
export const outlineToneClasses: Record<ToastTone, string> = {
  default: cn('border-outline bg-surface text-text-primary'),
  subtle: cn('border-outline bg-surface-accent text-text-primary'),
  success: cn(
    'border-validation-success-border bg-validation-success-muted text-validation-success'
  ),
  destructive: cn(
    'border-validation-destructive-border bg-validation-destructive-muted text-validation-destructive'
  ),
  warning: cn(
    'border-validation-warning-border bg-validation-warning-muted text-validation-warning'
  ),
  pro: cn('border-tier-pro-border bg-tier-pro-muted text-tier-pro-text'),
  practice: cn(
    'border-tier-practice-border bg-tier-practice-muted text-tier-practice-text'
  ),
};

// Width contract for the toast that lives inside Sonner's wrapper.
// - `w-[400px]`: target width on roomy viewports.
// - `max-w-[calc(100vw-2rem)]`: cap on tiny viewports so the 400px
//   target doesn't hang off the edge.
// - `mx-auto`: centers the toast within Sonner's mobile-mode wrapper
//   (which becomes wider than 400px and defaults to flex-start).
//   No-op above Sonner's mobile breakpoint, where the wrapper is
//   narrower than the toast and the auto margins collapse to 0.
export const solidBaseClasses = cn(
  'pointer-events-auto relative mx-auto flex w-[400px] max-w-[calc(100vw-2rem)] items-center gap-3',
  'overflow-hidden rounded-lg border px-3 py-2 shadow-lg'
);

export const outlineBaseClasses = cn(
  'pointer-events-auto relative mx-auto flex w-[400px] max-w-[calc(100vw-2rem)] items-center gap-3',
  'overflow-hidden rounded-lg border px-3 py-2 shadow-lg'
);

/**
 * Get toast tone classes based on variant and tone.
 * Default variant is `outline` — the standard callout treatment used by
 * almost every toast.
 */
export const getToastToneClasses = (
  variant: ToastVariant = 'outline',
  tone: ToastTone = 'default'
): string => {
  if (variant === 'solid') {
    return solidToneClasses[tone];
  }
  return outlineToneClasses[tone];
};

/**
 * Get toast base classes based on variant
 */
export const getToastBaseClasses = (variant: ToastVariant = 'outline'): string => {
  if (variant === 'solid') {
    return solidBaseClasses;
  }
  return outlineBaseClasses;
};
